import { all, call, select, takeLatest } from 'typed-redux-saga';

import { SELF } from '../../../shared/constants';
import { selectIsMeetingRoomIntegration } from '../../../shared/features/config/selectors';
import { parentFrameCommunicator } from '../../../shared/features/parent-frame-communicator';
import { ScrollStateChangedPayload } from '../../../shared/features/scroll-controller/types';
import { ChannelHolder } from '../../../shared/helpers/channelHolder';
import { createLogger } from '../../../shared/helpers/loggerFacade';
import { setIsChatLoading } from '../features/chat/slice';
import { updateParticipant, updateParticipants } from '../features/conference/slice';
import {
    setCurrentPage,
    setMaxDisplayParticipants,
    setStoreUserPreferredGridView,
} from '../features/goloom-video-grid/slice';
import { participantsScrollController } from '../features/sidebar/participants-scroll-controller';
import { setSidebarContent } from '../features/sidebar/slice';
import { canChangePage } from '../selectors';
import { SidebarContent } from '../types';
import { OutgoingEvents, UiStateChangedPayload } from './types';

const logger = createLogger('handleUiStateSync');

function* sendUiStateChanged(payload: UiStateChangedPayload) {
    try {
        yield* call(parentFrameCommunicator.sendMessage, '*', {
            event: OutgoingEvents.UI_STATE_CHANGED,
            payload,
        });
    } catch (error) {
        logger.error(error, {
            method: 'sendUiStateChanged',
            additional: { payload },
        });
    }
}

function isMediaEnabled(muted?: boolean) {
    return muted !== undefined ? !muted : undefined;
}

function* handleParticipantsUpdate({ payload: participantsByIds }: ReturnType<typeof updateParticipants>) {
    const selfParticipant = participantsByIds[SELF];

    if (!selfParticipant) {
        return;
    }

    yield* call(sendUiStateChanged, {
        videoEnabled: isMediaEnabled(selfParticipant.video?.muted),
        audioEnabled: isMediaEnabled(selfParticipant.audio?.muted),
        reaction: selfParticipant.reaction?.reactionType,
    });
}

function* handleParticipantUpdate({ payload: participant }: ReturnType<typeof updateParticipant>) {
    if (participant.id !== SELF) {
        return;
    }

    yield* call(sendUiStateChanged, {
        videoEnabled: isMediaEnabled(participant.video?.muted),
        audioEnabled: isMediaEnabled(participant.audio?.muted),
        reaction: participant.reaction?.reactionType,
    });
}

function* handleGridViewChange({ payload: { gridView } }: ReturnType<typeof setStoreUserPreferredGridView>) {
    yield* call(sendUiStateChanged, {
        gridView: gridView,
    });
}

function* handleSidebarContentChange({ payload: sidebarContent }: ReturnType<typeof setSidebarContent>) {
    yield* call(sendUiStateChanged, {
        chatVisible: sidebarContent === SidebarContent.Chat,
        participantsListVisible: sidebarContent === SidebarContent.Participants,
    });
}

function* handleChatLoadingChange({ payload: isChatLoading }: ReturnType<typeof setIsChatLoading>) {
    yield* call(sendUiStateChanged, {
        isChatLoading,
    });
}

function* handleGridPaginationChange() {
    const hasGridNextPage = yield* select(canChangePage, 1);
    const hasGridPrevPage = yield* select(canChangePage, -1);

    yield* call(sendUiStateChanged, {
        hasGridNextPage,
        hasGridPrevPage,
    });
}

function* handleScrollStateChange(payload: ScrollStateChangedPayload) {
    yield* call(sendUiStateChanged, {
        canScrollParticipantsListUp: payload.canScrollUp,
        canScrollParticipantsListDown: payload.canScrollDown,
    });
}

export function* handleUiStateSync() {
    const isMeetingRoomIntegration = yield* select(selectIsMeetingRoomIntegration);

    if (!isMeetingRoomIntegration) {
        return;
    }

    const channelHolder = new ChannelHolder();

    try {
        yield* all([
            takeLatest(updateParticipants, handleParticipantsUpdate),
            takeLatest(updateParticipant, handleParticipantUpdate),
            takeLatest(setStoreUserPreferredGridView, handleGridViewChange),
            takeLatest(setSidebarContent, handleSidebarContentChange),
            takeLatest(setIsChatLoading, handleChatLoadingChange),
            takeLatest(
                [setCurrentPage, updateParticipants, updateParticipant, setMaxDisplayParticipants],
                handleGridPaginationChange
            ),
            channelHolder.takeLatest(participantsScrollController.onScrollStateChanged, handleScrollStateChange),
        ]);
    } finally {
        channelHolder.close();
    }
}
